/**
 * Utility functions for API operations
 */

import type { UseQueryOptions } from '@tanstack/react-query'

/**
 * Generic query options type that preserves React Query's type inference
 */
export type QueryOptions<TData = unknown, TError = Error, TQueryKey = unknown> = Omit<
  UseQueryOptions<TData, TError, TData, (string | TQueryKey)[]>,
  'queryKey' | 'queryFn'
>

/**
 * Convert query parameters object to URL search string
 */
export function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        value.forEach(item => searchParams.append(key, String(item)))
      } else {
        searchParams.append(key, String(value))
      }
    }
  })

  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

/**
 * Check if a request payload is too large for GET request
 */
export function isRequestTooLarge(payload: any, maxLength: number = 2000): boolean {
  const payloadString = JSON.stringify(payload)
  return payloadString.length > maxLength
}

/**
 * Create a cache key for React Query based on endpoint and parameters
 */
export function createCacheKey(endpoint: string, params?: Record<string, any>): string[] {
  const baseKey = [endpoint]
  if (params && Object.keys(params).length > 0) {
    baseKey.push(JSON.stringify(params))
  }
  return baseKey
}

/**
 * Deep merge two objects (useful for merging filters)
 */
export function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const result = { ...target }

  Object.keys(source).forEach(key => {
    const sourceValue = source[key]
    const targetValue = target[key]

    if (sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
      // Type assertion to allow assignment to indexed type
      ; (result as any)[key] = deepMerge(targetValue || {}, sourceValue)
    } else {
      // Type assertion to allow assignment to indexed type
      ; (result as any)[key] = sourceValue
    }
  })

  return result
}

/**
 * Retry configuration for API requests
 */
export interface RetryConfig {
  retries?: number
  retryDelay?: (attemptIndex: number) => number
  retryCondition?: (error: any) => boolean
}

/**
 * Default retry configuration
 */
export const defaultRetryConfig: Required<RetryConfig> = {
  retries: 3,
  retryDelay: (attemptIndex: number) => Math.min(1000 * Math.pow(2, attemptIndex), 30000),
  retryCondition: (error: any) => {
    // Retry on network errors and 5xx status codes
    return !error.status || error.status >= 500
  },
}


/**
 * Long-term cache configuration for data that rarely change
 */
export const longTermCacheConfig = {
  staleTime: 24 * 60 * 60 * 1000, // 24 hours
  gcTime: 7 * 24 * 60 * 60 * 1000, // 7 days
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchInterval: false as const,
}

/**
 * Static cache configuration for React Query
 */
export const staticCacheConfig = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
}

/**
 * Dynamic cache configuration for frequently changing data
 */
export const dynamicCacheConfig = {
  staleTime: 30 * 1000, // 30 seconds
  gcTime: 5 * 60 * 1000, // 5 minutes
}

/**
 * Format error message for display to users
 */
export function formatApiError(error: any): string {
  if (error?.data?.message) {
    return error.data.message
  }

  if (error?.message) {
    return error.message
  }

  if (error?.status) {
    switch (error.status) {
      case 400:
        return 'Bad request. Please check your input.'
      case 401:
        return 'Authentication required. Please log in.'
      case 403:
        return 'Access denied. You do not have permission for this operation.'
      case 404:
        return 'Resource not found.'
      case 408:
        return 'Request timeout. Please try again.'
      case 429:
        return 'Too many requests. Please wait and try again.'
      case 500:
        return 'Server error. Please try again later.'
      case 502:
        return 'Bad gateway. Please try again later.'
      case 503:
        return 'Service unavailable. Please try again later.'
      default:
        return `Request failed with status ${error.status}.`
    }
  }

  return 'An unexpected error occurred. Please try again.'
}

/**
 * Type guard to check if an error is an API error
 */
export function isApiError(error: any): error is { status: number; message: string; data?: any } {
  return (
    error &&
    typeof error === 'object' &&
    'status' in error &&
    'message' in error &&
    typeof error.status === 'number' &&
    typeof error.message === 'string'
  )
}
